import { useEffect, useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Select } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { etapasAtivas } from '@/data/funis';
import { dados } from '@/data/porta';
import type { Funil, Profissional } from '@/data/tipos';
import type { Etiqueta } from '@/data/cadastros';

export type AcaoEmLote = 'profissional' | 'responsavel' | 'pipeline' | 'etiqueta';

const TEXTOS: Record<AcaoEmLote, { titulo: string; campo: string; feito: string }> = {
    profissional: { titulo: 'Trocar o profissional responsável', campo: 'Profissional responsável', feito: 'Profissional trocado' },
    responsavel: { titulo: 'Trocar o responsável', campo: 'Responsável', feito: 'Responsável trocado' },
    pipeline: { titulo: 'Mover no pipeline', campo: 'Etapa', feito: 'Leads movidos' },
    etiqueta: { titulo: 'Adicionar etiqueta', campo: 'Etiqueta', feito: 'Etiqueta aplicada' },
};

export interface AcaoEmLoteModalProps {
    acao: AcaoEmLote | null;
    /** Ids de paciente (contato) ou de lead, conforme a ação. */
    ids: string[];
    nomes: string[];
    onClose: () => void;
    onFeito: () => void;
}

/** As etapas ativas de todos os pipelines. Com mais de um, o nome do pipeline vai junto. */
function opcoesDeEtapa(funis: Funil[]): { value: string; label: string }[] {
    return funis.flatMap((f) => etapasAtivas(f).map((e) => ({
        value: e.id,
        label: funis.length > 1 ? `${f.nome} · ${e.titulo}` : e.titulo,
    })));
}

/**
 * As ações em lote de Contatos, feitas de verdade.
 *
 * Mostravam "Ação em lote registrada" e não mudavam nada. Quem assume vem da
 * equipe cadastrada, e a lista de quem vai mudar fica à vista antes do clique.
 * As etapas vêm dos pipelines da clínica, não de uma lista fixa.
 */
export function AcaoEmLoteModal({ acao, ids, nomes, onClose, onFeito }: AcaoEmLoteModalProps) {
    const aviso = useAviso();
    const idCampo = useId();
    const [equipe, setEquipe] = useState<Profissional[] | null>(null);
    const [funis, setFunis] = useState<Funil[] | null>(null);
    const [etiquetas, setEtiquetas] = useState<Etiqueta[] | null>(null);
    const [erroDaCarga, setErroDaCarga] = useState<string | null>(null);
    const [valor, setValor] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [aplicando, setAplicando] = useState(false);

    useEffect(() => {
        setValor('');
        setErro(null);
        if (!acao) return;
        let vivo = true;
        setErroDaCarga(null);
        if (acao === 'pipeline') {
            setFunis(null);
            dados().funis()
                .then((f) => { if (vivo) setFunis(f); })
                .catch((e: unknown) => { if (vivo) setErroDaCarga(e instanceof Error ? e.message : 'Não foi possível ler os pipelines.'); });
        } else if (acao === 'etiqueta') {
            setEtiquetas(null);
            dados().atendimentoECrm()
                .then((c) => { if (vivo) setEtiquetas(c.etiquetas.filter((e) => e.ativa)); })
                .catch((e: unknown) => { if (vivo) setErroDaCarga(e instanceof Error ? e.message : 'Não foi possível ler as etiquetas.'); });
        } else {
            setEquipe(null);
            dados().opcoesDoAgendamento()
                .then((o) => { if (vivo) setEquipe(o.profissionais); })
                .catch((e: unknown) => { if (vivo) setErroDaCarga(e instanceof Error ? e.message : 'Não foi possível ler a equipe.'); });
        }
        return () => { vivo = false; };
    }, [acao]);

    if (!acao) return null;

    const textos = TEXTOS[acao];
    const opcoes = acao === 'pipeline'
        ? opcoesDeEtapa(funis ?? [])
        : acao === 'etiqueta'
            ? (etiquetas ?? []).map((e) => ({ value: e.id, label: e.nome }))
            : (equipe ?? []).map((p) => ({ value: p.id, label: p.nome }));
    const carregado = acao === 'pipeline' ? funis !== null : acao === 'etiqueta' ? etiquetas !== null : equipe !== null;
    const quantos = `${ids.length} ${ids.length === 1 ? 'contato' : 'contatos'}`;

    const aplicar = async () => {
        setAplicando(true);
        setErro(null);
        try {
            const mudaram = acao === 'profissional'
                ? await dados().trocarProfissionalDosPacientes(ids, valor)
                : acao === 'etiqueta'
                    ? await dados().etiquetarContatos(ids, valor)
                    : acao === 'responsavel'
                        ? await dados().trocarResponsavelDosLeads(ids, valor)
                        : await dados().moverLeadsDeEtapa(ids, valor);
            aviso({ title: textos.feito, description: `${mudaram} de ${quantos}` });
            onFeito();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível aplicar.');
        } finally {
            setAplicando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={460} title={textos.titulo} description={`${quantos} selecionados`}
            dirty={valor !== ''}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void aplicar()} loading={aplicando} disabled={!valor}>Aplicar a {quantos}</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Alert tone="neutral" title="Vai mudar" dismissible={false}>{nomes.join(', ')}</Alert>
                {erroDaCarga && (
                    <Alert tone="danger" title={acao === 'pipeline' ? 'Os pipelines não carregaram' : acao === 'etiqueta' ? 'As etiquetas não carregaram' : 'A equipe não carregou'} dismissible={false}>
                        {erroDaCarga}
                    </Alert>
                )}
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <FormField label={textos.campo} htmlFor={idCampo} required>
                    <Select
                        id={idCampo} value={valor} options={opcoes}
                        disabled={!carregado}
                        onChange={(v) => { setValor(v); setErro(null); }}
                    />
                </FormField>
            </div>
        </Modal>
    );
}
